import Immutable from "immutable"
import type { MRT_ColumnDef, MRT_FilterFn, MRT_Row, MRT_SortingFn } from "mantine-react-table"

import { centsToCurrencyStr } from "shared/pac/helperFunctions"
import { computeColumnData } from "app/static/frontend/sheets/switches"
import {
    billNumberSort,
    congressionalDistrictSort,
    dateSort,
    formatNumber,
    integerSort,
    stringSort,
} from "app/static/frontend/sheets/helper-functions"
import { SUPPORTER_RECIPIENT_KEY, USER_RECIPIENT_KEY } from "shared/imports/sharedConstants"
import moment from "moment"
import { stanceWithCustomLabelByValue } from "shared/stance/helperFunctions"

import sheetConstants from "app/static/frontend/sheets/constants"
import { formatPercentageForDisplay } from "shared/imports/sharedHelperFunctions"

/**
 * Return a React-DOM safe version of column data to be rendered in cells.
 * Objects need to be stringified, but otherwise every other piece of data
 * can be displayed as-is
 *
 * @function
 * @param {Object} value the column data point for a cell
 * @returns {Object} A DOM-safe value to be displayed directly in the React DOM.
 */
export const extractDisplayValuesFromColumnValue = (value, columnData, allColumns, rowId) => {
    if (typeof value === "object") {
        return JSON.stringify(value)
    }

    if (value === null || value === undefined) {
        // some cells are calculated at the cell level currently, and not at the mantine level - we need to account for these or else
        // the filtering will not work as mantine will not actually contain cell data
        const immutableColumnData = Immutable.fromJS(columnData)
        const computedData = computeColumnData(immutableColumnData, allColumns).toJS()

        return computedData && Object.keys(computedData).length ? computedData[rowId] : null
    }

    return value
}
/**
 * Returns the correct Quorum SafedSearch ID value to use as a direct index into Quorum Column data.
 * Sometimes we deal with either a row object or a row instsance from MRT and this helps ease dilineation
 *
 * @function
 * @param rowOrRowModel the row object from the Quorum sheet model or a row instance from mantine-react-table
 * @returns {Number} integer id to index directly into column data
 */

// MRT_FilterFn uses the Tanstack Table type Row explicitly, which we don't directly have available,
// So we do some Typescript "magic" (read: hacky stuff) to extract it from the types we do have available
type Row = Parameters<Exclude<MRT_FilterFn, string>>[0]

export const getSafedSearchRowId = (rowOrRowModel: Row | MRT_Row | Record<string, never>): number =>
    // @ts-expect-error Typescript doesn't like the combined types here
    rowOrRowModel?.original?.safedSearchId ?? rowOrRowModel.safedSearchId

// logic abstracted from AssignmentCell.tsx
export const assignmentCellNameBuilder = (column, recipientKey, recipientFirstNameKey, recipientLastNameKey) => (row) =>
    extractAssignmentCellNames(
        column.data && column.data[getSafedSearchRowId(row)],
        recipientKey,
        recipientFirstNameKey,
        recipientLastNameKey,
    )

export const extractAssignmentCellNames = (assignments, recipientKey, recipientFirstNameKey, recipientLastNameKey) => {
    if (
        assignments?.length > 0 &&
        assignments[0]?.hasOwnProperty(`${recipientKey}__${recipientFirstNameKey}`) &&
        assignments[0]?.hasOwnProperty(`${recipientKey}__${recipientLastNameKey}`)
    ) {
        return assignments
            .map((assignment) =>
                [
                    assignment[`${recipientKey}__${recipientFirstNameKey}`],
                    assignment[`${recipientKey}__${recipientLastNameKey}`],
                ]
                    .join(" ")
                    .trim(),
            )
            .join(", ")
    } else {
        return assignments
            ?.map((assignment) => assignment?.name)
            ?.filter((value) => !!value)
            ?.join(", ")
    }
}

interface BuildMantineColumFiltersProps {
    columnData: any
    canFitInMemory: boolean
    alignmentTypes: any
    dataType: any
    allImmutableColumns?: any
    canFilterOnBackend: boolean
}

interface MantineColumnFilterInterface {
    accessorFn?: MRT_ColumnDef["accessorFn"]
    filterVariant?: MRT_ColumnDef["filterVariant"]
    filterFn?: MRT_FilterFn
    mantineFilterMultiSelectProps?: MRT_ColumnDef["mantineFilterMultiSelectProps"]
    mantineFilterRangeSliderProps?: MRT_ColumnDef["mantineFilterRangeSliderProps"]
    mantineFilterDateInputProps?: MRT_ColumnDef["mantineFilterDateInputProps"]
    minSize?: number
    enableColumnFilter?: boolean
}

/**
 * Builds the filter properties for each column of a sheet.
 *
 * @function
 * @param {Object} columnData the column data we are creating the filters for
 * @returns {Immutable.Map} an object containing the filter data for mantine
 */
export const buildMantineColumnFilters = ({
    columnData,
    canFitInMemory,
    alignmentTypes,
    dataType,
    allImmutableColumns,
    canFilterOnBackend,
}: BuildMantineColumFiltersProps): MantineColumnFilterInterface => {
    if (!columnData) {
        return { filterVariant: "text", filterFn: "contains" }
    }

    if (!canFitInMemory && !canFilterOnBackend) {
        return { enableColumnFilter: false }
    }

    const columnType = columnData.column_type || DjangIO.app.userdata.spreadsheet.types.SheetColumnType.field.value

    let enumSelectData = []

    const immutableColumnData = Immutable.fromJS(columnData)
    const computedData = computeColumnData(immutableColumnData, allImmutableColumns)?.toJS()

    const getFieldObjMultiSelectEnumData = (field) => {
        if (canFitInMemory) {
            // Collect enum values we have in the data
            const values = Immutable.Set(Object.values(columnData.data))
            return (
                DjangIO.enumFromField(field)
                    .filter_enums_by_values(values)
                    // extract value / label from enum, some enums overwhelm mantine filters for unknown reasons.
                    .map(({ value, label }) => ({ value, label }))
            )
        }
        // For large sheets, when values are not known beforehand
        return DjangIO.enumFromField(field).items()
    }

    const getMultiSelectEnumData = (options) => {
        if (columnData) {
            // Get enum values from columnData and filter options
            const values = Immutable.Set(Object.values(columnData.data))
            return options.filter_enums_by_values(values)
        }
        // If neither columnData nor field is provided, use options directly
        return options.items()
    }

    const getColumnRange = (): [number, number] => {
        // if we are a big sheet, we should have a range passed from the backend
        if (columnData.range) {
            return columnData.range
        }

        const immutableColumnData = Immutable.fromJS(columnData)
        const computedData = computeColumnData(immutableColumnData, allImmutableColumns).toJS() as object
        const arrayOfValues = Object.values(computedData).map((item) => {
            if (typeof item === "object" && item !== null) {
                return item.value || null
            }
            return item
        })

        if (computedData && arrayOfValues.length) {
            const topRange = Math.max(...arrayOfValues) || 100
            const lowerRange = Math.min(...arrayOfValues) || 0
            return [
                lowerRange === -Infinity ? Number.MIN_SAFE_INTEGER : lowerRange,
                topRange === Infinity ? Number.MAX_SAFE_INTEGER : topRange, // make sure we don't send Infinity
            ]
        }

        return [0, 0]
    }

    const buildMultiSelectFilter = (filterData): MantineColumnFilterInterface => ({
        filterVariant: "multi-select",
        filterFn: "multiSelect",
        mantineFilterMultiSelectProps: { data: filterData },
    })

    const columnDataTypeToMantineFilters = (): MantineColumnFilterInterface => {
        switch (columnData.column_data_type) {
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
                return { filterVariant: "checkbox", filterFn: "checkbox" }
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
                return {
                    filterVariant: "date-range",
                    filterFn: "dateRange",
                    minSize: sheetConstants.MANTINE_DATE_COLUMN_MIN_WIDTH,
                }
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.long.value:
                return { filterFn: "amountRange", filterVariant: "range" }
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
                return { filterFn: "dateRange", filterVariant: "range" }
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value:
                return {
                    accessorFn: (row) =>
                        columnData?.data?.[getSafedSearchRowId(row)] &&
                        Number(parseInt(columnData.data[getSafedSearchRowId(row)])),
                    filterVariant: "range",
                    filterFn: "currencyAmountRange",
                }
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.percent.value:
                const range = getColumnRange()
                return {
                    accessorFn: (row) => {
                        const value = computedData?.[getSafedSearchRowId(row)]
                        if (value !== null && !isNaN(Number(value))) {
                            return Number(value)
                        }
                        return value
                    },
                    filterVariant: "range-slider",
                    filterFn: "amountRange",
                    mantineFilterRangeSliderProps: { min: range[0], minRange: 0, max: range[1], step: 0.01 },
                }

            default:
                return { filterVariant: "text", filterFn: "contains" }
        }
    }

    const buildCustomFieldFilters = (): MantineColumnFilterInterface => {
        const tagType = columnData.custom_tag?.tag_type

        switch (tagType) {
            case DjangIO.app.person.types.TagType.number.value:
                const range = getColumnRange()
                return {
                    accessorFn: (row) => {
                        const value =
                            computedData?.[getSafedSearchRowId(row)]?.value || computedData?.[getSafedSearchRowId(row)]
                        if (value !== null && !isNaN(Number(value))) {
                            return Number(value)
                        }
                        return value
                    },
                    filterVariant: "range-slider",
                    filterFn: "amountRange",
                    mantineFilterRangeSliderProps: { min: range[0], minRange: 0, max: range[1], step: 0.1 },
                }
            case DjangIO.app.person.types.TagType.date.value:
                return {
                    accessorFn: (row) => {
                        const rowData = computedData?.[getSafedSearchRowId(row)]

                        if (rowData && typeof rowData === "object" && rowData.hasOwnProperty("value")) {
                            return rowData["value"]
                        }

                        return rowData
                    },
                    filterVariant: "date-range",
                    filterFn: "dateRange",
                    mantineFilterDateInputProps: { clearable: true },
                    minSize: sheetConstants.MANTINE_DATE_COLUMN_MIN_WIDTH,
                }
            case DjangIO.app.person.types.TagType.single_option_list.value:
            case DjangIO.app.person.types.TagType.multi_options_list.value:
                enumSelectData = columnData.custom_tag?.options_new || false
                if (enumSelectData) {
                    return buildMultiSelectFilter(
                        Object.entries(enumSelectData).map(([slug, properties]) => ({
                            value: slug,
                            label: properties.value,
                        })),
                    )
                } else {
                    return { filterVariant: "text", filterFn: "contains" }
                }
            case DjangIO.app.person.types.TagType.boolean.value:
                return {
                    accessorFn: (row) =>
                        computedData?.[getSafedSearchRowId(row)]?.value || computedData?.[getSafedSearchRowId(row)],
                    filterVariant: "checkbox",
                    filterFn: "checkbox",
                }
            default:
                return { filterVariant: "text", filterFn: "contains" }
        }
    }

    const buildFieldTypeFilters = (): MantineColumnFilterInterface => {
        const fieldObj = DjangIO.findField(columnData.field, dataType) || {}

        if (fieldObj?._is_enum_field) {
            // handle enum fields
            enumSelectData = getFieldObjMultiSelectEnumData(fieldObj)
            return buildMultiSelectFilter(enumSelectData)
        }

        if (
            columnData.column_data_type &&
            columnData.column_data_type !== DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.field.value
        ) {
            // handle non-field type columns
            return columnDataTypeToMantineFilters()
        }

        switch (fieldObj && fieldObj.field_type) {
            case DjangIO.djangio.types.FieldType.BooleanField.value:
            case DjangIO.djangio.types.FieldType.NullBooleanField.value:
                return { filterVariant: "checkbox", filterFn: "checkbox" }
            case DjangIO.djangio.types.FieldType.IntegerField.value:
                return { filterVariant: "range" }
            case DjangIO.djangio.types.FieldType.DateTimeField.value:
            case DjangIO.djangio.types.FieldType.QuorumDateTimeField.value:
            case DjangIO.djangio.types.FieldType.DateField.value:
                return {
                    filterVariant: "date-range",
                    filterFn: "dateRange",
                    minSize: sheetConstants.MANTINE_DATE_COLUMN_MIN_WIDTH,
                }
            default:
                return { filterVariant: "text", filterFn: "contains" }
        }
    }

    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
            return buildCustomFieldFilters()
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer_field.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.field.value:
            return buildFieldTypeFilters()
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_bill.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_person.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.models.Region)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sponsorship_record.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.bill.models.SponsorType)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_interaction_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_public_organization.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_office.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.userdata.types.InteractionType)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.committee_membership.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.caucus_membership.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.committee.models.CommitteeMemberRole)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.voting_record.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.vote.models.VoteResultType)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stance_record.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.userdata.types.SheetStanceVoteResultType)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_priority.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_priority.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.userdata.types.LegislativePriorityType)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_stance.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_stance.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.userdata.types.LegislativeStanceType)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_thumb.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.userdata.spreadsheet.types.ThumbType)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_field.value:
            return columnData.column_data_type ===
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value
                ? { filterVariant: "range" }
                : { filterVariant: "text" }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.rsvp_status_of_custom_event.value:
            enumSelectData = getMultiSelectEnumData(DjangIO.app.custom_event.types.RSVPStatus)
            return buildMultiSelectFilter(enumSelectData)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value:
            enumSelectData = alignmentTypes.toJS().map((type) => ({ label: type.label, value: type.id }))
            return buildMultiSelectFilter(enumSelectData)

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.member_birth_month_day.value:
            return {
                filterVariant: "date-range",
                filterFn: "dateRangeNoYear",
                mantineFilterDateInputProps: {
                    clearable: true,
                    maxLevel: "year",
                    valueFormat: "MMMM DD",
                    yearLabelFormat: "",
                },
            }
        // NOTE: We cannot specify a custom accessorFn here, as the cell needs direct access to the
        // data for the sake of editing. So we specify custom filter functions here
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment_primary.value:
            return { filterVariant: "text", filterFn: "containsUserAssignment" }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_assignment.value:
            return { filterVariant: "text", filterFn: "containsSupporterAssignment" }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.percentage_of_columns.value:
            const range = getColumnRange().map((limit) => limit * 100)
            return {
                accessorFn: (row) => {
                    const value = computedData?.[getSafedSearchRowId(row)]
                    if (value !== null && !isNaN(Number(value))) {
                        return Number(value)
                    }
                    return value
                },
                filterVariant: "range-slider",
                filterFn: (row, id, filterValue) => {
                    const [min, max] = filterValue.map((val) => val / 100)
                    const rowValue = computedData?.[getSafedSearchRowId(row)]
                    return rowValue >= min && rowValue <= max
                },
                mantineFilterRangeSliderProps: { min: range[0], minRange: 0, max: range[1], step: 0.01 },
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.in_issue.value:
            return {
                filterFn: "containsInIssue",
                filterVariant: "text",
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.contact_organizations.value:
            return {
                filterVariant: "text",
                filterFn: "containsOrganizationName",
            }
        default:
            return columnDataTypeToMantineFilters()
    }
}

/**
 * Returns the appropriate data for the datum column depending on the sheet's
 * datatype
 * @param {*} dataType A QuorumDataType enum value indicating the type of the sheet
 * @param {*} row The row data for the Mantine row
 * @param {*} props React component props
 * @returns A text value for the given row's datum column
 */
export const datumAccessorForDataType = (dataType, row) => {
    switch (dataType) {
        case DjangIO.app.models.QuorumDataType.supporter.value:
            return row.name || "Unnamed Contact"
        case DjangIO.app.models.QuorumDataType.vote.value:
            return row.question
        case DjangIO.app.models.QuorumDataType.user.value:
            return row.user_info__full_name || `${row.first_name} ${row.last_name}`
        default:
            return row[DjangIO.app.models.QuorumDataType.by_value(dataType).display_key]
    }
}

interface BuildMantineSortingFunctionsProps {
    column: any
    sheetDataType: number
    canFitInMemory: boolean
    canSortOnBackend: boolean
}

interface MantineSortingFunctionsInterface {
    enableSorting?: boolean
    sortingFn?: MRT_SortingFn
    sortUndefined?: false | -1 | 1
}

/*
Given a column data type and a sort direction, returns a function that sorts
a list of rows in that direction.
 */
export const buildMantineSortingFunctions = ({
    column,
    sheetDataType,
    canFitInMemory,
    canSortOnBackend,
}: BuildMantineSortingFunctionsProps): MantineSortingFunctionsInterface => {
    const { column_data_type: columnDataType, column_type: columnType, field: columnField = undefined } = column
    if (!canFitInMemory && !canSortOnBackend) {
        return {
            enableSorting: false,
        }
    }
    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
            const customTag = column.custom_tag
            switch (customTag.tag_type) {
                case DjangIO.app.person.types.TagType.date.value:
                    return {
                        sortingFn: (rowA, rowB) =>
                            dateSort({ shouldReverse: true })(
                                column?.data?.[getSafedSearchRowId(rowA)],
                                column?.data?.[getSafedSearchRowId(rowB)],
                            ),
                        sortUndefined: 1,
                    }
                case DjangIO.app.person.types.TagType.single_option_list.value:
                case DjangIO.app.person.types.TagType.multi_options_list.value:
                    return {
                        sortingFn: (rowA, rowB) =>
                            stringSort({ shouldReverse: true })(
                                getDisplayValueForCustomTag(column?.data?.[getSafedSearchRowId(rowA)], customTag),
                                getDisplayValueForCustomTag(column?.data?.[getSafedSearchRowId(rowB)], customTag),
                            ),
                        sortUndefined: 1,
                    }
                default:
                    break
            }
            break

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.member_birth_month_day.value:
            // only consider the month and day when sorting this column
            // assuming yearMonthDay looks like "YYYY-MM-DD"
            return {
                sortingFn: (rowA, rowB) => {
                    const dateA = moment(column?.data?.[getSafedSearchRowId(rowA)]).date()
                    const monthA = moment(column?.data?.[getSafedSearchRowId(rowA)]).month()
                    const dateB = moment(column?.data?.[getSafedSearchRowId(rowB)]).date()
                    const monthB = moment(column?.data?.[getSafedSearchRowId(rowB)]).month()

                    // simple hueristic to ensure leap years' dates and non-leap-years' dates are sorted correctly
                    return integerSort({ shouldReverse: false })(dateA + monthA * 100, dateB + monthB * 100)
                },
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value:
            // sort by the number of people assigned
            return {
                sortingFn: (rowA, rowB) =>
                    integerSort({ shouldReverse: false })(
                        column?.data?.[getSafedSearchRowId(rowA)]?.length,
                        column?.data?.[getSafedSearchRowId(rowB)]?.length,
                    ),
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.contact_organizations.value:
            return {
                sortingFn: (rowA, rowB) => {
                    const a = column?.data?.[getSafedSearchRowId(rowA)]?.cell_value
                    const b = column?.data?.[getSafedSearchRowId(rowB)]?.cell_value
                    return stringSort({ shouldReverse: true })(a, b)
                },
                sortUndefined: 1,
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.committee_membership.value:
            // sort primarily by the order field
            // lookup the enum value using by_value since we're only passed the enum
            // we're checking the order field of the enum and passing that value, or if it doesn't have one
            // we'll pass Infinity or undefined
            return {
                sortingFn: (rowA, rowB) => {
                    const roleEnumClass = DjangIO.app.committee.models.CommitteeMemberRole

                    // sort primarily by the order field
                    // lookup the enum value using by_value since we're only passed the enum
                    // we're checking the order field of the enum and passing that value, or if it doesn't have one
                    // we'll pass undefined
                    const a_order = roleEnumClass.by_value(column?.data?.[getSafedSearchRowId(rowA)])?.order
                    const b_order = roleEnumClass.by_value(column?.data?.[getSafedSearchRowId(rowB)])?.order
                    return integerSort({ shouldReverse: false })(a_order, b_order)
                },
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.future_hearing_date.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.hearing_date.value:
            // hearing date rows could have multiple dates for a cell separated by a comma, so sort the column by the
            // first date within each string, which will be the future-most date
            return {
                sortingFn: (rowA, rowB) => {
                    const a_date = new Date(column?.data?.[getSafedSearchRowId(rowA)]?.split(", ")[0])
                    const b_date = new Date(column?.data?.[getSafedSearchRowId(rowB)]?.split(", ")[0])
                    return dateSort({ shouldReverse: false })(a_date, b_date)
                },
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.in_issue.value:
            return {
                sortingFn: (rowA, rowB) => {
                    const aName = column.data?.[getSafedSearchRowId(rowA)]
                        ?.map((issue) => issue.name)
                        ?.filter((i) => i)
                        ?.join(", ")
                    const bName = column.data?.[getSafedSearchRowId(rowB)]
                        ?.map((issue) => issue.name)
                        ?.filter((i) => i)
                        ?.join(", ")
                    return stringSort({ shouldReverse: false })(aName, bName)
                },
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_bill.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_person.value:
            // sort primarily by the region_name field
            // lookup the enum value using by_value since we're only passed the enum
            // we're checking the region_name field of the enum and passing that value, or if it doesn't have one
            // we'll pass undefined
            return {
                sortingFn: (rowA, rowB) => {
                    const aVal = column?.data?.[getSafedSearchRowId(rowA)]
                    const a_region_name = DjangIO.app.models.Region.by_value(aVal)?.region_name
                    const bVal = column?.data?.[getSafedSearchRowId(rowB)]
                    const b_region_name = DjangIO.app.models.Region.by_value(bVal)?.region_name

                    return stringSort({ shouldReverse: true })(a_region_name, b_region_name)
                },
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_public_organization.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_office.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_interaction_type.value:
            // sort primarily by the order field
            // lookup the enum value using by_value since we're only passed the enum
            // we're checking the order field of the enum and passing that value, or if it doesn't have one
            // we'll pass undefined
            return {
                sortingFn: (rowA, rowB) => {
                    const a_order = DjangIO.app.userdata.types.InteractionType.by_value(
                        column?.data?.[getSafedSearchRowId(rowA)],
                    )?.order
                    const b_order = DjangIO.app.userdata.types.InteractionType.by_value(
                        column?.data?.[getSafedSearchRowId(rowB)],
                    )?.order
                    return integerSort({ shouldReverse: false })(a_order, b_order)
                },
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_county_legislator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_assemblymember.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_delegate.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_municipal_legislator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_councilmember.value:
            return {
                sortingFn: (rowA, rowB) => {
                    const aName = column?.data?.[getSafedSearchRowId(rowA)]?.[0]?.name
                    const bName = column?.data?.[getSafedSearchRowId(rowB)]?.[0]?.name
                    return stringSort({ shouldReverse: false })(aName, bName)
                },
                sortUndefined: 1,
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_charity_additional_info.value:
            return {
                sortingFn: (rowA, rowB) => {
                    const a = column?.data?.[getSafedSearchRowId(rowA)]
                    const b = column?.data?.[getSafedSearchRowId(rowB)]

                    // always put Not Editable value at the bottom
                    const isANotEditable = a === "Not Editable"
                    const isBNotEditable = b === "Not Editable"
                    if (isANotEditable && !isBNotEditable) {
                        return 1
                    }
                    if (!isANotEditable && isBNotEditable) {
                        return -1
                    }
                    if (isANotEditable && isBNotEditable) {
                        return 0
                    }

                    return stringSort({ shouldReverse: true })(a, b)
                },
                sortUndefined: 1,
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_charity_anonymous.value:
            return {
                sortingFn: (rowA, rowB) => {
                    const a = column?.data?.[getSafedSearchRowId(rowA)]
                    const b = column?.data?.[getSafedSearchRowId(rowB)]

                    // always put Not Editable value at the bottom
                    const isANotEditable = a === "Not Editable"
                    const isBNotEditable = b === "Not Editable"
                    if (isANotEditable && !isBNotEditable) {
                        return 1
                    }
                    if (!isANotEditable && isBNotEditable) {
                        return -1
                    }
                    if (isANotEditable && isBNotEditable) {
                        return 0
                    }

                    return stringSort({ shouldReverse: false })(a, b)
                },
                sortUndefined: 1,
            }

        default:
            // break out of this switch statement and move on to try to match on column data type
            break
    }

    switch (columnDataType) {
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
            return {
                sortingFn: (rowA, rowB) => {
                    const a = column?.data?.[getSafedSearchRowId(rowA)]
                    const b = column?.data?.[getSafedSearchRowId(rowB)]
                    // Prevents flipping of values by returning 1 when the values match (keeps 'em in order)
                    if (!!a === !!b) {
                        return 0
                    }
                    return !a && b ? -1 : 1
                },
                sortUndefined: false,
            }

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.field.value:
            // depending on the data type of the field, we need to handle sorting differently

            // see if we can find the field
            try {
                const fieldObj = DjangIO.findField(columnField, sheetDataType)
                if (fieldObj && fieldObj._is_enum_field) {
                    const enumObj = DjangIO.enumFromField(fieldObj)
                    const attr = fieldObj.enum_sorting_attr || "label"
                    // make sure that if the value is null or empty we don't get errors
                    return {
                        sortingFn: (rowA, rowB, columnId) => {
                            const label_a = enumObj.by_value(column?.data?.[getSafedSearchRowId(rowA)])?.[attr] ?? null
                            const label_b = enumObj.by_value(column?.data?.[getSafedSearchRowId(rowB)])?.[attr] ?? null
                            return stringSort({ shouldReverse: true })(label_a, label_b)
                        },
                        sortUndefined: 1,
                    }
                } else if (
                    sheetDataType === DjangIO.app.userdata.spreadsheet.types.SheetDataType.bill.value &&
                    fieldObj &&
                    fieldObj.name === "label"
                ) {
                    return {
                        sortingFn: (rowA, rowB, columnId) => {
                            return billNumberSort({ shouldReverse: true })(
                                column?.data?.[getSafedSearchRowId(rowA)],
                                column?.data?.[getSafedSearchRowId(rowB)],
                            )
                        },
                        sortUndefined: 1,
                    }
                } else if (
                    sheetDataType === DjangIO.app.userdata.spreadsheet.types.SheetDataType.person.value &&
                    fieldObj &&
                    fieldObj.verbose_name === "Congressional District"
                ) {
                    // TODO: { sortingFn: "alphanumeric", sortUndefined: true } should handle this just fine
                    // but not worth changing this late before release
                    return {
                        sortingFn: (rowA, rowB) => {
                            return congressionalDistrictSort({ shouldReverse: true })(
                                column?.data?.[getSafedSearchRowId(rowA)],
                                column?.data?.[getSafedSearchRowId(rowB)],
                            )
                        },
                        sortUndefined: 1,
                    }
                } else {
                    return {
                        sortingFn: (rowA, rowB) => {
                            return stringSort({ shouldReverse: true })(
                                column?.data?.[getSafedSearchRowId(rowA)],
                                column?.data?.[getSafedSearchRowId(rowB)],
                            )
                        },
                        sortUndefined: 1,
                    }
                }
            } catch (error) {
                return {
                    sortingFn: (rowA, rowB) => {
                        return stringSort({ shouldReverse: true })(
                            column?.data?.[getSafedSearchRowId(rowA)],
                            column?.data?.[getSafedSearchRowId(rowB)],
                        )
                    },
                    sortUndefined: 1,
                }
            }

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
            return {
                sortingFn: (rowA, rowB) =>
                    dateSort({ shouldReverse: false })(
                        column?.data?.[getSafedSearchRowId(rowA)],
                        column?.data?.[getSafedSearchRowId(rowB)],
                    ),
                sortUndefined: 1,
            }

        default:
            return { sortingFn: "alphanumeric", sortUndefined: 1 }
    }
}

/**
 * Extracted from selectIsStatCell.
 * Returns result depends whether or not the column is a field column.  If it is we use the column's FieldType from the
 * field object. Otherwise we'll look at the ColumnDataType from the SheetColumnType.
 *
 * Note: we don't check the ColumnDataType on Field Columns because the DataType will be ColumnCategory.field and this
 * value will not determine if the column has number values.  Instead we'll look at it's FieldType (if it's not an enum)
 */
export const isStatColumn = (
    columnDataType: number,
    field: { _is_enum_field?: boolean; field_type?: number },
    columnType: number,
): boolean => {
    if (columnType === DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value) {
        return false
    }
    if (field && field._is_enum_field) {
        return false
    }
    if (field && field.field_type) {
        const fieldTypeValue = field.field_type
        // @ts-expect-error FieldType has a collision between the types defined in app/ and the types defined in djangio/
        return DjangIO.djangio.types.FieldType.by_value(fieldTypeValue).should_show_column_math
    }
    if (columnDataType) {
        // @ts-expect-error ColumnDataType isn't in the right path to be automatically picked up by Djangio Typescript annotations
        return DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.by_value(columnDataType)
            .should_show_column_math
    }
    return false
}

/**
 * Extracted from selectSheetsFooterMathValue.
 * Returns the aggregated sum for all of the applicable row values for the given
 * column
 * @param {*} rowData the sheet rowData prop containing a list of lists of row values
 * @param {*} column the column object to calculate the footer for
 * @param {*} allColumns list of all of the column objects in the sheet
 * @returns the sum for the all of this column's values, if available
 */
export const calculateFooterValue = (rowData, column, allColumns) => {
    const getRowDataDict = (rowData) => {
        return rowData ? rowData.reduce((result, item) => result.set(item.get("id"), true), new Map()) : new Map()
    }
    const getComputedColumnData = (column, allColumns) => column && allColumns && computeColumnData(column, allColumns)
    const getFilteredColumnData = (rowDataDict, columnData) =>
        // some id pks are not coercable to number, such as Transaction UUIDField/uuid.uuid4 UUIDs
        // (i.e., #000034bc-ea7e-4824-93f4-019ff08bcd9d),
        // so in that case, we just don't convert to number
        columnData ? columnData.filter((_, id) => rowDataDict.get(Number(id) || id)) : new Map()

    const rowDataDict = getRowDataDict(rowData)
    const computedColumnData = getComputedColumnData(Immutable.fromJS(column), allColumns)
    const filteredColumnData = getFilteredColumnData(rowDataDict, computedColumnData)
    return column.customDataSum ?? filteredColumnData?.reduce((a, b) => Number(a) + Number(b))
}

/**
 * Returns a label containing the aggregated sum of values for a column if the
 * column is a stat column
 * @param {*} column the column object to create the footer label for
 * @param {*} allColumns all column objects in the sheet
 * @param {*} rowData a list of lists of row values
 * @param {*} sheetDataType QuorumDataType of the sheet
 * @returns text containing the sum of the column values or null if not a stat column
 */
export const getMathFooterLabel = (column, allColumns, rowData, sheetDataType) => {
    const fieldObj = DjangIO.findField(column.field, sheetDataType) || {}
    const isStat = isStatColumn(column.column_data_type, fieldObj, column.column_type)
    const footerValue = calculateFooterValue(rowData, column, allColumns)

    if (isStat && footerValue) {
        let formattedSum
        if (column.column_data_type === DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value) {
            formattedSum = centsToCurrencyStr(footerValue)
        } else {
            formattedSum = new Intl.NumberFormat().format(footerValue)
        }
        return formattedSum
    }
}

export const handleRenameColumn = (id, columnData, renameColumnAction) => {
    swal({
        title: "Rename Column",
        content: {
            element: "input",
            attributes: {
                placeholder: "Rename Column...",
                value: columnData.name,
            },
        },
        buttons: [true, true],
        closeOnClickOutside: false,
    }) // in this case we don't need to check for inputValue === ""
        .then((inputValue) => {
            inputValue && renameColumnAction(id, columnData.id, inputValue)
        })
}

export const isColumnColoringEnabled = (column, sheetDataType) => {
    const tagType = column?.custom_tag?.tag_type
    const fieldObject = DjangIO.findField(column?.field, sheetDataType)

    // The column type itself may disallow coloring
    if (
        column?.column_type &&
        !DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(column?.column_type).coloring_enabled
    ) {
        return false
    }
    // if a custom field, switch on the tagType instead of the columnDataType
    // custom tags pass their slugs as the cellValues
    if (tagType) {
        switch (tagType) {
            case DjangIO.app.person.types.TagType.single_option_list.value:
            case DjangIO.app.person.types.TagType.string.value:
            case DjangIO.app.person.types.TagType.number.value:
                return true
            default:
                // multi_option_list is unsupported because the behavior cannot be determined
                return false
        }
    } else if (fieldObject && Object.keys(fieldObject).length) {
        if (fieldObject._is_enum_field) {
            return true
        }
        switch (fieldObject.field_type) {
            case DjangIO.djangio.types.FieldType.CharField.value:
            case DjangIO.djangio.types.FieldType.TextField.value:
            case DjangIO.djangio.types.FieldType.IntegerField.value:
            case DjangIO.djangio.types.FieldType.FloatField.value:
            case DjangIO.djangio.types.FieldType.DecimalField.value:
            case DjangIO.djangio.types.FieldType.DateField.value:
                return true
            default:
                return false
        }
    } else {
        switch (column?.column_data_type) {
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
            case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
                return true
            default:
                return false
        }
    }
}

export const getDisplayValueForCustomTag = (value, customTag) => {
    const getValue = (key) => key && customTag?.options_new?.[key]?.value
    const isArchived = (key) => key && customTag?.options_new?.[key]?.archived
    // choose many custom field column
    if (Array.isArray(value)) {
        return value
            .filter((each) => !isArchived(each))
            .map((each) => getValue(each))
            .join(", ")
    }
    // primitive type custom field column
    if ((typeof value !== "object" || value === null) && customTag?.options_new === null) {
        return value
    }
    // JSON custom field column that has a string, number, boolean, or date value
    if (typeof value === "object" && customTag?.options_new === null) {
        return value?.value
    }
    // choose one custom field from note column
    if (typeof value === "object" && customTag?.options_new !== null) {
        return getValue(value?.value)
    }
    // choose one custom field column
    return getValue(value)
}

export const buildColumnGlobalSearchProperties = ({
    column,
    sheetDataType,
    alignmentTypes,
    stanceOptions,
    columns,
}) => {
    // setup helpers, constants, builders, etc.
    const dateFormatterBuilder = (format, value) => {
        if (typeof value === "number") {
            const numericValue = Number(value)
            return moment.unix(numericValue)
        }
        return moment(value).local().format(format)
    }
    const GENERIC_DATE_FILTER = {
        globalSearchFunction: (row) => {
            const cellValue = column?.data?.[getSafedSearchRowId(row)]
            let value
            if (typeof cellValue !== "object" || cellValue === null) {
                value = cellValue
            } else {
                value = cellValue.value
            }
            if (value) {
                return dateFormatterBuilder("L", value)
            }
        },
    }
    const GENERIC_DATE_TIME_FILTER = {
        globalSearchFunction: (row) => {
            const value = column?.data?.[getSafedSearchRowId(row)]
            if (value) {
                return dateFormatterBuilder(sheetConstants.DATE_TIME_FORMAT, value)
            }
        },
    }
    const DISABLE_GLOBAL_FILTER = {
        enableGlobalFilter: false,
    }
    const buildEnumAccessorFunction = (column, e, displayKey = "label") => {
        return {
            globalSearchFunction: (row) => {
                const val = column?.data[getSafedSearchRowId(row)]
                if (val) {
                    return e.by_value(val)?.[displayKey] ?? ""
                }
            },
        }
    }

    const immutableColumnData = Immutable.fromJS(column)
    const computedData = computeColumnData(immutableColumnData, columns)?.toJS()

    const buildCustomTagCellAccessorFn = (column, tagType, customTag, getDisplayValue) => {
        switch (tagType) {
            case DjangIO.app.person.types.TagType.boolean.value:
                return DISABLE_GLOBAL_FILTER
            case DjangIO.app.person.types.TagType.number.value:
            case DjangIO.app.person.types.TagType.single_option_list.value:
            case DjangIO.app.person.types.TagType.multi_options_list.value:
            case DjangIO.app.person.types.TagType.string.value:
                return {
                    globalSearchFunction: (row) => {
                        const val = column?.data?.[getSafedSearchRowId(row)]
                        // if it's a choose many custom field, call getDisplayValue on the array of choices
                        if (Array.isArray(val?.value)) {
                            return getDisplayValue(val?.value, customTag)
                        }
                        return getDisplayValue(val, customTag)
                    },
                }
            case DjangIO.app.person.types.TagType.date.value:
                return GENERIC_DATE_FILTER
            default:
                break
        }
    }

    const { column_type: columnType, column_data_type: dataType, field: columnField, custom_tag: customTag } = column
    const tagType = customTag?.tag_type

    const field = DjangIO.findField(columnField, sheetDataType)

    // begin casework, see `cell-component-selectors.jsx` and subsequent components for primary structure / logic of globalSearchFunctions
    if (field) {
        if (field._is_enum_field) {
            const e = DjangIO.enumFromField(field)
            let displayKey = "label"
            if (DjangIO.app.userdata.spreadsheet.types.FieldOptionType.by_value(columnType)?.enum_field) {
                displayKey = DjangIO.app.userdata.spreadsheet.types.FieldOptionType.by_value(columnType)?.enum_field
            } else if (field.sheets_display_key) {
                displayKey = field.sheets_display_key
            }

            return buildEnumAccessorFunction(column, e, displayKey)
        }
        if (!dataType) {
            switch (field.field_type) {
                case DjangIO.djangio.types.FieldType.DateField.value:
                    return GENERIC_DATE_FILTER
                case DjangIO.djangio.types.FieldType.QuorumDateTimeField.value:
                    return GENERIC_DATE_TIME_FILTER
                default:
                    break
            }
        }
    }

    // if (columnType && DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).search_filters) {}
    // Assignment Cells have special cases to build their strings, but all rather similar
    if (
        [
            DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value,
            DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value,
            DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_assignment.value,
            DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment_primary.value,
            DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_assignment.value,
        ].includes(columnType)
    ) {
        switch (columnType) {
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_assignment.value:
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment_primary.value:
                return {
                    globalSearchFunction: assignmentCellNameBuilder(
                        column,
                        USER_RECIPIENT_KEY,
                        "first_name",
                        "last_name",
                    ),
                }
            case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_assignment.value:
                return {
                    globalSearchFunction: assignmentCellNameBuilder(
                        column,
                        SUPPORTER_RECIPIENT_KEY,
                        "firstname",
                        "lastname",
                    ),
                }
        }
    }
    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.contact_organizations.value:
            return {
                globalSearchFunction: (row) => column.data && column.data?.[getSafedSearchRowId(row)]?.cell_value,
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer.value:
            return { globalSearchFunction: (row) => column.data?.[getSafedSearchRowId(row)]?.name }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
            const searchProps = buildCustomTagCellAccessorFn(column, tagType, customTag, getDisplayValueForCustomTag)
            if (searchProps) {
                return searchProps
            }
            break
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_office.value:
            return GENERIC_DATE_TIME_FILTER
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_date_only.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_office_date_only.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.next_interaction.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.date_of_last_public_mention.value:
            return GENERIC_DATE_FILTER
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.committee_membership.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.caucus_membership.value:
            return buildEnumAccessorFunction(column, DjangIO.app.committee.models.CommitteeMemberRole)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.voting_record.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.vote_direction.value:
            return DISABLE_GLOBAL_FILTER
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sponsorship_record.value:
            return buildEnumAccessorFunction(column, DjangIO.app.bill.models.SponsorType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_public_organization.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_office.value:
            return buildEnumAccessorFunction(column, DjangIO.app.userdata.types.InteractionType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.vote_stance.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stance_record.value:
            // icons
            return DISABLE_GLOBAL_FILTER
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_stance.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_stance.value:
            // return MantineBillStanceCell
            return {
                globalSearchFunction: (row) => {
                    const val = column.data && column.data[getSafedSearchRowId(row)]
                    return stanceWithCustomLabelByValue(stanceOptions.toJS(), val)?.label
                },
            }

            break
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_priority.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_priority.value:
            return buildEnumAccessorFunction(column, DjangIO.app.userdata.types.LegislativePriorityType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.cook_pvi.value:
            // can still filter this column, but the display is vastly different than the underlying data.
            // disabling to prevent confusion.
            return DISABLE_GLOBAL_FILTER
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.member_birth_month_day.value:
            return {
                globalSearchFunction: (row) =>
                    column.data && moment(column.data?.[getSafedSearchRowId(row)]).format("MMMM DD"),
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_councilmember.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_delegate.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_assemblymember.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_county_legislator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_municipal_legislator.value:
            return {
                globalSearchFunction: (row) =>
                    column.data?.[getSafedSearchRowId(row)]
                        ?.map((person) => person.name)
                        ?.filter((i) => i)
                        .join(", "),
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.rsvp_status_of_custom_event.value:
            return buildEnumAccessorFunction(column, DjangIO.app.custom_event.types.RSVPStatus)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.in_issue.value:
            return {
                globalSearchFunction: (row) =>
                    column.data?.[getSafedSearchRowId(row)]
                        ?.map((issue) => issue.name)
                        ?.filter((i) => i)
                        ?.join(", "),
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_text.value:
            // should be fine as text, this cell replaces the raw text with @ tags...
            break
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_thumb.value:
            return DISABLE_GLOBAL_FILTER
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value:
            return {
                globalSearchFunction: (row) => {
                    const cellValue = column.data && column.data[getSafedSearchRowId(row)]
                    return alignmentTypes.toJS().find(({ id }) => id === cellValue)?.label
                },
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_interaction_type.value:
            return buildEnumAccessorFunction(column, DjangIO.app.userdata.types.InteractionType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.transaction_credit_ledger_account_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.transaction_debit_ledger_account_type.value:
            return buildEnumAccessorFunction(column, DjangIO.app.ledger.types.LedgerAccountType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.percentage_of_columns.value:
            return {
                globalSearchFunction: (row) => {
                    const value = computedData?.[getSafedSearchRowId(row)]
                    if (value !== null && !isNaN(Number(value))) {
                        return formatPercentageForDisplay(Number(value) * 100)
                    }
                    return value
                },
            }
        default:
            break
    }
    // continue on - try to match on columnDataType
    switch (dataType) {
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
            return {
                globalSearchFunction: (row) => {
                    const value = column?.data?.[getSafedSearchRowId(row)]
                    if (value !== null && !isNaN(Number(value))) {
                        return formatNumber(Number(value))
                    }
                    return value
                },
            }

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.percent.value:
            return {
                globalSearchFunction: (row) => {
                    const value = column?.data?.[getSafedSearchRowId(row)]
                    if (value !== null && !isNaN(Number(value))) {
                        return formatPercentageForDisplay(Number(value))
                    }
                    return value
                },
            }

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
            return {
                globalSearchFunction: (row) => {
                    const value = computedData?.[getSafedSearchRowId(row)]
                    if (value !== null && !isNaN(Number(value))) {
                        return formatNumber(Number(value))
                    }
                    return value
                },
            }
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
            return DISABLE_GLOBAL_FILTER
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
            return GENERIC_DATE_FILTER
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
            return {
                globalSearchFunction: (row) => {
                    const value = column?.data?.[getSafedSearchRowId(row)]
                    return Number(dateFormatterBuilder("YYYY", value))
                },
            }
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.link.value:
            return DISABLE_GLOBAL_FILTER
        /* NOTE: LinkCell.tsx shows icons and labels for the URL, not actual URLs. so this is being disabled
                 like other icon cells. If Link cells are modified in the future, this globalSearchFunction needs to be
                 reenabled.
            return {
                globalSearchFunction: (row) => {
                    const value = column?.data?.[getSafedSearchRowId(row)]
                    if (Array.isArray(value)) {
                        return (
                            value
                                // TODO: is this fallback even worth it?
                                .map((urlObj) => urlObj.label ?? urlObj.url)
                                .filter((x) => !!x)
                                .join(", ")
                        )
                    }
                    return value?.label ?? value?.url
                },
            }
            */

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value:
            return {
                globalSearchFunction: (row) =>
                    Number(parseFloat(column?.data?.[getSafedSearchRowId(row)]) / 100).toFixed(2),
            }
    }
    return { globalSearchFunction: (row) => column.data && column.data[getSafedSearchRowId(row)] }
}

// handles edge cases where the filter value is `[null, null]` which
// returns as `true` for column.getIsFiltered()
export const isColumnActivelyFiltered = (column) => {
    const filterValues = column.getFilterValue()
    if (Array.isArray(filterValues)) {
        // multi-part filters can function with just one entry
        return filterValues.some((fVal) => Boolean(fVal))
    }
    return Boolean(filterValues)
}

// MRT handles some data-types inconsistently with how our redux state expects values to be
// handled. This does a quick check and corerces values to the column-data-type defined on the column
export const coerceValueToDataType = (val, col) => {
    if (col?.meta?.columnDataType === DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value) {
        // MRT passes in "true" and "false" directly, need to convert them to boolean values.
        const boolMap = { false: false, true: true }
        return boolMap[val] ?? !!val
    }
    return val
}

export const buildGetGroupingValue = (column): { getGroupingValue?: MRT_ColumnDef["getGroupingValue"] } => {
    const columnType = column.column_type
    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.contact_organizations.value:
            return { getGroupingValue: (row) => column.data && column.data[getSafedSearchRowId(row)]?.cell_value }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.in_issue.value:
            return {
                getGroupingValue: (row) => {
                    const rowData = column.data?.[getSafedSearchRowId(row)]
                    return rowData?.[0]?.id
                },
            }
        default:
            return undefined
    }
}
